import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import TwoFactorSetup from "../components/TwoFactorSetup";
import AriaSettings from "../components/AriaSettings";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Check, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data));
  }, []);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const r = await api.post("/payments/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const featuresFor = (slug) => {
    if (slug === "starter")
      return [
        "All 11 clinical modules",
        "50 consultations / month",
        "Case history & PDF export",
        "Email support",
      ];
    return [
      "Everything in Starter",
      "Unlimited consultations",
      "Priority AI response",
      "Up to 5 doctor seats",
      "Treatment-plan templates",
    ];
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Layout>
      <div className="kicker">Account</div>
      <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2" data-testid="account-title">
        Subscription & Billing
      </h1>

      <div className="gold-divider my-8" />

      <div className="bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-6 mb-10" data-testid="current-plan-card">
        <div className="kicker mb-3">Current Status</div>
        <div className="flex items-baseline gap-3">
          <div className="font-serif text-2xl text-[var(--pa-text)]">
            {user?.subscription_status === "active"
              ? `${user.subscription_plan?.toUpperCase()} Plan`
              : "No active subscription"}
          </div>
          {user?.subscription_status === "active" && (
            <span className="text-xs text-emerald-400">● Active</span>
          )}
        </div>
        <div className="mt-2 text-sm text-[var(--pa-text-muted)]">
          {user?.subscription_status === "active"
            ? "Thank you for being a member. You have full access to all modules."
            : "You're currently on the free tier. Subscribe to unlock unlimited consultations."}
        </div>
        <button
          onClick={refresh}
          data-testid="refresh-status"
          className="mt-4 text-xs text-[var(--pa-gold)] hover:underline"
        >
          Refresh status
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((p) => {
          const isCurrent = user?.subscription_status === "active" && user?.subscription_plan === p.slug;
          return (
            <div
              key={p.slug}
              data-testid={`plan-card-${p.slug}`}
              className={`rounded-lg p-8 border ${
                p.slug === "practice"
                  ? "border-[var(--pa-gold)] bg-[var(--pa-surface)]"
                  : "border-[var(--pa-border)] bg-[var(--pa-surface)]"
              }`}
            >
              <div className="font-serif text-2xl text-[var(--pa-text)]">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-serif text-4xl text-[var(--pa-text)]">${Math.round(p.amount)}</span>
                <span className="text-sm text-[var(--pa-text-muted)]">/month</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {featuresFor(p.slug).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[var(--pa-text-muted)]">
                    <Check size={14} className="text-[var(--pa-gold)] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                disabled={isCurrent || busy === p.slug}
                onClick={() => subscribe(p.slug)}
                data-testid={`subscribe-${p.slug}`}
                className={`mt-7 w-full font-semibold rounded-md px-6 py-3 transition flex items-center justify-center gap-2 ${
                  isCurrent
                    ? "bg-[var(--pa-bg-2)] text-[var(--pa-text-muted)] cursor-not-allowed"
                    : p.slug === "practice"
                    ? "bg-[var(--pa-gold)] text-[var(--pa-bg)] hover:bg-[var(--pa-gold-hover)]"
                    : "border border-[var(--pa-gold)] text-[var(--pa-gold)] hover:bg-[var(--pa-gold)]/10"
                }`}
              >
                {busy === p.slug ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Redirecting…
                  </>
                ) : isCurrent ? (
                  "Current plan"
                ) : (
                  `Subscribe to ${p.name}`
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Security */}
      <div className="mt-12 max-w-xl">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={16} className="text-[var(--pa-gold)]" />
          <div className="kicker">Security</div>
        </div>

        {/* Two-factor authentication */}
        <div className="mb-8">
          <TwoFactorSetup onChange={refresh} />
        </div>

        <h2 className="font-serif text-xl text-[var(--pa-text)]">Change password</h2>
        <p className="text-xs text-[var(--pa-text-muted)] mt-1">
          You will stay signed in on this device. Other sessions will continue with the
          existing token until it expires.
        </p>
        <form
          onSubmit={changePassword}
          data-testid="change-password-form"
          className="mt-5 space-y-4 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-5"
        >
          <div>
            <label className="kicker block mb-1.5">Current password</label>
            <input
              type="password"
              required
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              data-testid="pw-current"
              className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
            />
          </div>
          <div>
            <label className="kicker block mb-1.5">New password</label>
            <input
              type="password"
              required
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              data-testid="pw-new"
              className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
            />
            <div className="text-[10px] text-[var(--pa-text-dim)] mt-1">Min. 6 characters</div>
          </div>
          <div>
            <label className="kicker block mb-1.5">Confirm new password</label>
            <input
              type="password"
              required
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              data-testid="pw-confirm"
              className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
            />
          </div>
          <button
            type="submit"
            disabled={pwSaving}
            data-testid="pw-submit"
            className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-5 py-2.5 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 flex items-center gap-2"
          >
            {pwSaving ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Saving…
              </>
            ) : (
              "Update password"
            )}
          </button>
        </form>
      </div>

      {/* ARIA Settings */}
      <div className="mt-12 max-w-xl">
        <AriaSettings />
      </div>
    </Layout>
  );
}
